import type { SavedMeasurement } from "../viewer/MeasureTool";

/**
 * Client for the DB-backed measurements API (`server/src/routes/measurements.ts`) — the
 * sole store for saved measurements. This replaces the previous scheme of one `.txt` file
 * per measurement inside a `Ausmass - Geo3D Viewer` Trimble Connect folder entirely: nothing
 * here reads or writes project files any more, and no such folder is created.
 *
 * Every call requires the caller's Trimble bearer token (`getToken`, normally
 * `WorkspaceBridge.accessToken`) — the server re-verifies it against Trimble's own identity
 * provider and checks the whitelist before touching anything (see `server/src/auth.ts`).
 */
export class MeasurementsStore {
  constructor(private readonly getToken: () => Promise<string>) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    const response = await fetch(`/api/measurements${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...init.headers },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}) as { error?: string });
      throw new Error(body.error ?? `Measurements API ${response.status} on ${path}`);
    }
    return (await response.json()) as T;
  }

  /** Every measurement saved against `datasetKey` (see `App.datasetKey`). */
  async list(datasetKey: string): Promise<SavedMeasurement[]> {
    const { measurements } = await this.request<{ measurements: SavedMeasurement[] }>(
      `?datasetKey=${encodeURIComponent(datasetKey)}`,
    );
    return measurements;
  }

  /** Creates or overwrites (by `measurement.id`) a saved measurement. */
  async save(datasetKey: string, measurement: SavedMeasurement): Promise<void> {
    await this.request(`/${encodeURIComponent(measurement.id)}`, {
      method: "PUT",
      body: JSON.stringify({ ...measurement, datasetKey }),
    });
  }

  async remove(id: string): Promise<void> {
    await this.request(`/${encodeURIComponent(id)}`, { method: "DELETE" });
  }
}

/** Stable key a dataset's measurements are scoped to — a project id plus the folder its data
 *  lives in, matching what used to make one Ausmass folder per dataset. `null` when either
 *  half is unknown (local files, a share link with no resolvable folder) — measurements
 *  can't be scoped, so saving is unavailable, same as before. */
export function datasetKeyFor(projectId: string | null | undefined, dataFolderId: string | null): string | null {
  return projectId && dataFolderId ? `${projectId}:${dataFolderId}` : null;
}
